/* OWN (Stoffeya): free-shipping bar.
 * - Re-renders on every cart change (PUB_SUB_EVENTS.cartUpdate from product-form.js / cart.js).
 * - Optionally swaps the threshold to the visitor's IP-detected country via Shopify's
 *   /browsing_context_suggestions.json (only for countries we actually ship to).
 * Markup + initial state come from snippets/free-shipping-bar.liquid.
 */
if (!customElements.get('free-shipping-bar')) {
  class FreeShippingBar extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.mode || 'bar'; // 'bar' (progress) | 'line' (totals summary line)
      this.textEl = this.querySelector('.free-shipping-bar__text');
      this.fillEl = this.querySelector('.free-shipping-bar__fill');
      this.hintEl = this.querySelector('.free-shipping-bar__hint');
      this.lineValueEl = this.querySelector('.free-shipping-bar__line-value');

      try {
        this.thresholds = JSON.parse(this.dataset.thresholds || '{}');
      } catch (e) {
        this.thresholds = {};
      }
      this.marketCountry = this.dataset.country || '';
      this.country = this.marketCountry;
      this.countryName = '';
      this.total = Number(this.dataset.total) || 0;
      this.shippable = (this.dataset.countries || '').split(',').filter(Boolean);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.onCartUpdate(event));
      }
      if (this.dataset.geo === 'true') this.applyDetectedCountry();
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    thresholdFor(country) {
      const t = this.thresholds;
      return Object.prototype.hasOwnProperty.call(t, country) ? Number(t[country]) : Number(t['*']) || 0;
    }

    onCartUpdate(event) {
      const data = event && event.cartData;
      // cart/change.js + cart/update.js return the full cart; cart/add.js returns only the line item.
      if (data && Array.isArray(data.items) && typeof data.total_price === 'number') {
        this.total = data.total_price;
        this.render();
        return;
      }
      const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
      fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } })
        .then((res) => (res.ok ? res.json() : null))
        .then((cart) => {
          if (cart && typeof cart.total_price === 'number') {
            this.total = cart.total_price;
            this.render();
          }
        })
        .catch(() => {});
    }

    static detectCountry() {
      if (FreeShippingBar.geoPromise) return FreeShippingBar.geoPromise;
      FreeShippingBar.geoPromise = (async () => {
        try {
          const cached = sessionStorage.getItem('stoffeya:geo-country');
          if (cached) return JSON.parse(cached);
        } catch (e) {
          /* storage unavailable */
        }
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const res = await fetch(`${root}browsing_context_suggestions.json`, {
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) return null;
        const json = await res.json();
        const c = json && json.detected_values && json.detected_values.country;
        const geo = c && c.handle ? { code: String(c.handle).toUpperCase(), name: c.name || '' } : null;
        try {
          sessionStorage.setItem('stoffeya:geo-country', JSON.stringify(geo));
        } catch (e) {
          /* storage unavailable */
        }
        return geo;
      })().catch(() => null);
      return FreeShippingBar.geoPromise;
    }

    applyDetectedCountry() {
      FreeShippingBar.detectCountry().then((geo) => {
        if (!geo || !geo.code || geo.code === this.marketCountry) return;
        if (!this.shippable.includes(geo.code)) return; // we don't ship there — keep the market threshold
        if (this.thresholdFor(geo.code) === this.thresholdFor(this.marketCountry)) return;
        this.country = geo.code;
        this.countryName = geo.name;
        this.render();
      });
    }

    render() {
      const threshold = this.thresholdFor(this.country);
      if (!(threshold > 0)) {
        this.hidden = true;
        return;
      }
      this.hidden = false;

      const remaining = threshold - this.total;
      const reached = this.total > 0 && remaining <= 0;
      this.classList.toggle('is-reached', reached);

      if (this.mode === 'line') {
        if (this.lineValueEl) {
          this.lineValueEl.textContent = reached
            ? this.dataset.textShippingFree || ''
            : this.dataset.textShippingCalculated || '';
        }
        return;
      }
      if (!this.textEl || !this.fillEl) return;

      let html;
      if (this.total <= 0) {
        html = (this.dataset.textEmpty || '').replace('[amount]', this.money(threshold));
      } else if (reached) {
        html = this.dataset.textReached || '';
      } else {
        html = (this.dataset.textRemaining || '').replace('[amount]', `<strong>${this.money(remaining)}</strong>`);
      }
      this.textEl.innerHTML = html;
      this.fillEl.style.width = `${Math.min(100, Math.round((this.total / threshold) * 100))}%`;

      if (this.hintEl) {
        if (this.country !== this.marketCountry && this.countryName) {
          this.hintEl.textContent = (this.dataset.textCountry || '').replace('[country]', this.countryName);
          this.hintEl.hidden = false;
        } else {
          this.hintEl.hidden = true;
        }
      }
    }

    // Mirrors Liquid's `money` filter for the shop's money_format.
    money(cents) {
      const format = this.dataset.moneyFormat || '€{{amount_with_comma_separator}}';
      const value = Math.max(0, cents) / 100;
      const fmt = (decimals, thousands, decimal) => {
        const [int, frac] = value.toFixed(decimals).split('.');
        const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return frac !== undefined ? `${grouped}${decimal}${frac}` : grouped;
      };
      return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
        switch (key) {
          case 'amount_no_decimals':
            return fmt(0, ',', '.');
          case 'amount_with_comma_separator':
            return fmt(2, '.', ',');
          case 'amount_no_decimals_with_comma_separator':
            return fmt(0, '.', ',');
          case 'amount_with_apostrophe_separator':
            return fmt(2, "'", '.');
          case 'amount_no_decimals_with_space_separator':
            return fmt(0, ' ', '.');
          case 'amount_with_space_separator':
            return fmt(2, ' ', ',');
          case 'amount_with_period_and_space_separator':
            return fmt(2, ' ', '.');
          default:
            return fmt(2, ',', '.');
        }
      });
    }
  }
  FreeShippingBar.geoPromise = null;

  customElements.define('free-shipping-bar', FreeShippingBar);
}
